import React, { useState } from "react";
import {
  useGetBrokerageBarChartDataQuery,
  useGetBrokerageSortingChartDataQuery,
} from "../materialEntered/materialApi";
import { Col, Row } from "react-bootstrap";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import useTranslator from "../../../hooks/useTranslator";

const BrokerageDashboardChart = () => {
  const [barFilter, setBarFilter] = useState("monthly");
  const [pieFilter, setPieFilter] = useState("monthly");
  const { _t } = useTranslator();

  const FILTER_OPTIONS = ["Today", "Weekly", "Monthly"];
  const filterMap = {
    Today: "hourly",
    Weekly: "weekly",
    Monthly: "monthly",
  };

  const { data: barChartData } = useGetBrokerageBarChartDataQuery({
    filter: barFilter,
  });
  const { data: pieChart } = useGetBrokerageSortingChartDataQuery({
    filter: pieFilter,
  });

  const rawData = pieChart?.data?.data || [];

  const getValue = (name) => rawData.find((item) => item.name === name)?.y || 0;

  const entered = getValue("Kg Entered in Sorting");
  const selected = getValue("Kg Selected");
  const rejected = getValue("Kg Rejected");
  const leftToProcess = Math.max(entered - selected - rejected, 0);

  const simplifiedPieData = [
    {
      name: _t("kgs_left", "KG's Left to be Processed"),
      y: leftToProcess,
      color: "#46C389",
    },
    {
      name: _t("kgs_selected", "KG's Selected"),
      y: selected,
      color: "#FFBC55",
    },
    {
      name: _t("kgs_rejected", "KG's Rejected"),
      y: rejected,
      color: "#FE6565",
    },
  ];

  const hasBarData =
    Array.isArray(barChartData?.data?.series) &&
    barChartData.data.series.length > 0;

  const options = {
    chart: { type: "column" },
    title: { text: null, align: "left" },
    xAxis: {
      categories: hasBarData ? barChartData.data.categories : [_t("no_data", "No Data")],
      labels: {
        style: { fontSize: "12px", color: "#8C97A7" },
      },
    },
    yAxis: { title: { text: _t("cases", "Cases") } },
    legend: { align: "center", verticalAlign: "top", layout: "horizontal" },
    tooltip: { shared: true },
    plotOptions: {
      column: {
        grouping: true,
        borderWidth: 0,
        dataLabels: { enabled: false },
      },
    },
    series: hasBarData
      ? barChartData.data.series.map((series) => ({
        name: series?.name || "",
        data: Array.isArray(series?.data) ? [...series.data] : [],
        color:
          series?.name === "Total Kg"
            ? "#f5a623"
            : series?.name === "Accepted Kg"
              ? "#28a745"
              : "#dc3545",
      }))
      : [{ name: _t("no_data", "No Data"), data: [0], color: "#E8E8E8" }],
  };

  const chartOptions = {
    chart: {
      plotBackgroundColor: null,
      plotBorderWidth: null,
      plotShadow: false,
      type: "pie",
    },
    title: {
      text: "",
    },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.percentage:.1f}%</b>",
    },
    accessibility: {
      point: {
        valueSuffix: "%",
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: false,
        },
        showInLegend: true,
      },
    },
    legend: {
      layout: "vertical",
      align: "right",
      verticalAlign: "bottom",
      itemMarginBottom: 5,
      itemStyle: {
        color: "#8C97A7",
        fontSize: "14px",
      },
    },
    series: [
      {
        name: _t("data", "Data"),
        colorByPoint: true,
        data: simplifiedPieData,
      },
    ],
  };

  return (
    <Row className="mb-3">
      <Col lg={6}>
        <div className="cmn_card">
          <div className="overview_header d-flex gap-2 align-items-center">
            <h6 className="mb-0 flex-grow-1">{_t("supplier_data_overview", "Supplier Data Overview")}</h6>
            <ul className="chart_filter">
              {FILTER_OPTIONS.map((item, index) => (
                <li
                  key={index}
                  onClick={() => setBarFilter(filterMap[item])}
                  className={filterMap[item] === barFilter ? "active" : ""}
                >
                  {_t(item)}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-2">
            <HighchartsReact highcharts={Highcharts} options={options} />
          </div>
        </div>
      </Col>
      <Col lg={6}>
        <div className="cmn_card">
          <div className="overview_header d-flex gap-2 align-items-center">
            <h6 className="mb-0 flex-grow-1">{_t("sorting_overview", "Sorting Overview")}
            </h6>
            <ul className="chart_filter">
              {FILTER_OPTIONS.map((item, index) => (
                <li
                  key={index}
                  onClick={() => setPieFilter(filterMap[item])}
                  className={filterMap[item] === pieFilter ? "active" : ""}
                >
                  {_t(item)}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-2 position-relative">
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default BrokerageDashboardChart;
